// WO-60 — Admin → Market research: ANTHONY's background research of the
// Projects and Companies databases.
// What waits for a click (stage, value, new projects / companies, weak sources),
// what went live by itself (with Undo), the money, and a "check one now" box.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Sparkles, Check, X, Undo2, ExternalLink, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { safeUrl } from '../../lib/safeUrl';
import { pathId } from '../../lib/pathId';
import { getIndustryLabel } from '../../constants/industries'; // WO-74

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELD = {
  stage: 'Stage', value: 'Value (USD million)', start_date: 'Start year', end_date: 'End year',
  city: 'City', region: 'Region', stakeholder: 'New stakeholder', latest_update: 'Latest check note',
  sectors: 'Industries (main first)',
  news_link: 'Linked news', website: 'Website', linkedin: 'LinkedIn page', description: 'Description',
  country: 'Head-office country', new_project: 'NEW PROJECT', new_company: 'NEW COMPANY',
};

async function readError(res) {
  const data = await res.json().catch(() => ({}));
  return typeof data.detail === 'string' ? data.detail : `The server answered ${res.status}.`;
}

const money = (n) => `US$${Number(n || 0).toFixed(2)}`;
const when = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

function show(field, v) {
  if (v === null || v === undefined || v === '') return '—';
  if (field === 'value') return `${v.min ?? 0} – ${v.max ?? 0}`;
  if (field === 'stakeholder') return `${v.role}: ${v.name}`;
  if (field === 'latest_update') return v.text || '—';
  if (field === 'sectors') return (Array.isArray(v) && v.length) ? v.map((id) => getIndustryLabel(id)).join(' + ') : '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function Sources({ list }) {
  const items = (list || []).filter((s) => s && safeUrl(s.url));
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
      {items.map((s) => {
        let host = s.url;
        try { host = new URL(s.url).hostname.replace(/^www\./, ''); } catch (e) { /* keep */ }
        return (
          <a key={s.url} href={safeUrl(s.url)} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-1 text-primary hover:underline">
            {host}{s.verified === false ? ' (unverified)' : ''}<ExternalLink className="w-3 h-3" />
          </a>
        );
      })}
    </div>
  );
}

function RecordLink({ ch }) {
  const id = ch.created_entity_id || ch.entity_id;
  const base = ch.entity_type === 'companies' ? '/dashboard/companies/' : '/dashboard/projects/';
  const label = `${ch.ref_id ? ch.ref_id + ' · ' : ''}${ch.name || ''}`;
  return id ? <Link to={base + pathId(id)} className="font-medium hover:underline">{label}</Link> : <span className="font-medium">{label}</span>;
}

function NewProject({ v }) {
  return (
    <div className="text-sm space-y-1">
      <p><b>{v.name}</b> · {v.country}{v.city ? ` · ${v.city}` : ''} · stage <b>{v.stage}</b> · USD {v.value_min}–{v.value_max} million{v.value_note ? ` (${v.value_note})` : ''}</p>
      {(v.start_year || v.end_year) && <p>Years: {v.start_year || '?'} – {v.end_year || '?'}</p>}
      <p className="text-muted-foreground">{v.description}</p>
      {(v.stakeholders || []).length > 0 && (
        <p>Stakeholders: {v.stakeholders.map((s) => `${s.role}: ${s.name}${s.company_id ? ' (in database)' : ' (new)'}`).join('; ')}</p>
      )}
    </div>
  );
}

function ChangeRow({ ch, onApprove, onReject, onUndo, busy }) {
  const isNew = ch.kind === 'new_project' || ch.kind === 'new_company';
  return (
    <div className="p-3 rounded-lg bg-muted/30 space-y-2" data-testid="research-change">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={isNew ? 'default' : 'outline'}>{FIELD[ch.field] || ch.field}</Badge>
        <RecordLink ch={ch} />
        {ch.confidence && !isNew && <span className="text-xs text-muted-foreground">confidence {ch.confidence}</span>}
        <span className="text-xs text-muted-foreground ml-auto">{when(ch.created_at)}</span>
      </div>
      {ch.kind === 'new_project' ? <NewProject v={ch.new_value || {}} /> : ch.kind === 'new_company' ? (
        <p className="text-sm">{ch.new_value?.role}: <b>{ch.new_value?.name}</b></p>
      ) : (
        <p className="text-sm"><span className="text-muted-foreground line-through mr-2">{show(ch.field, ch.old_value)}</span>→ <b>{show(ch.field, ch.new_value)}</b></p>
      )}
      {ch.note && <p className="text-xs text-muted-foreground">{ch.note}</p>}
      {(ch.possible_duplicates || []).length > 0 && (
        <p className="text-xs text-amber-600">Similar names already in the database: {ch.possible_duplicates.map((d) => `${d.ref_id} ${d.name} (${Math.round(d.score * 100)}%)`).join('; ')}</p>
      )}
      {ch.failure && <p className="text-xs text-red-600">Not applied: {ch.failure}</p>}
      <Sources list={ch.sources} />
      <div className="flex gap-2">
        {onApprove && <Button size="sm" disabled={busy} onClick={() => onApprove(ch)}><Check className="w-4 h-4 mr-1" />{ch.kind === 'new_project' ? 'Approve (with its new companies)' : 'Approve'}</Button>}
        {onReject && <Button size="sm" variant="outline" disabled={busy} onClick={() => onReject(ch)}><X className="w-4 h-4 mr-1" />Reject</Button>}
        {onUndo && ch.field !== 'news_link' && !isNew && <Button size="sm" variant="outline" disabled={busy} onClick={() => onUndo(ch)}><Undo2 className="w-4 h-4 mr-1" />Undo</Button>}
      </div>
    </div>
  );
}

// WO-61 — every research setting the server accepts, visible and editable here
// (until now some could only be changed through the settings route).
const MODEL_LABEL = {
  'gpt-5.6-luna': 'Luna — cheaper, a little less thorough',
  'gpt-5.6-terra': 'Terra — several times the cost, digs deeper',
};
const NUM_FIELDS = [
  ['interval_minutes', 'Minutes between rounds', 'Each round checks one card and reads a few news items. Lower = faster, same daily money limit.', 2, 240],
  ['max_searches_per_check', 'Web searches per card (maximum)', 'Each search costs US$0.01. More searches can find more, and cost more.', 1, 10],
  ['recheck_days', 'Check a card again after (days)', 'For live projects and companies.', 7, 730],
  ['recheck_days_complete', 'Check a complete project again after (days)', '', 30, 1460],
  ['company_every', 'Every Nth check is a company', '0 = only projects.', 0, 20],
  ['max_new_per_day', 'New projects researched from the news, per day (maximum)', 'Each one waits for your approval.', 0, 50],
  ['news_per_tick', 'News items read per round', '0 = do not read the news.', 0, 30],
];

// About how much one card costs with a model: ~22,000 words read in, ~1,000 written,
// ~2.5 searches (measured on the live app, 09/10/2026).
function costPerCard(s, model) {
  const p = (s.prices || {})[model] || [2, 12];
  const searches = Math.min(2.5, Number(s.max_searches_per_check) || 2.5);
  return (22000 * p[0] + 1000 * p[1]) / 1e6 + searches * (Number(s.search_price_per_1000) || 10) / 1000;
}

function SettingsCard({ s, budget, busy, onSave }) {
  const [f, setF] = useState(null);
  useEffect(() => {
    setF({
      model: s.model, extract_model: s.extract_model, reasoning_effort: s.reasoning_effort,
      ...Object.fromEntries(NUM_FIELDS.map(([k]) => [k, String(s[k])])),
      blocked: (s.blocked_domains || []).join('\n'),
    });
  }, [s]);
  if (!f) return null;
  const models = Array.from(new Set([...Object.keys(s.prices || {}), s.model, s.extract_model]));
  const per = costPerCard({ ...s, max_searches_per_check: f.max_searches_per_check }, f.model);
  const perMonth = per > 0 ? Math.floor(Number(budget || s.monthly_budget_usd) / per) : 0;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = () => {
    const patch = {};
    if (f.model !== s.model) patch.model = f.model;
    if (f.extract_model !== s.extract_model) patch.extract_model = f.extract_model;
    if (f.reasoning_effort !== s.reasoning_effort) patch.reasoning_effort = f.reasoning_effort;
    for (const [k] of NUM_FIELDS) {
      if (String(f[k]).trim() !== String(s[k])) patch[k] = Number(f[k]);
    }
    const doms = f.blocked.split(/[\s,]+/).map((d) => d.trim()).filter(Boolean);
    if (doms.join('\n') !== (s.blocked_domains || []).join('\n')) patch.blocked_domains = doms;
    if (!Object.keys(patch).length) { toast.message('Nothing changed.'); return; }
    onSave(patch);
  };

  const field = 'w-full md:w-72';
  return (
    <Card className="border-0 bg-card/50" data-testid="research-settings">
      <CardHeader>
        <CardTitle>How ANTHONY works</CardTitle>
        <CardDescription>All the research settings. A change applies from the next round. Every change is checked by the server; a wrong value is refused with an explanation.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div className="grid md:grid-cols-2 gap-4">
          <label className="space-y-1">
            <span className="block font-medium">AI model for the web research</span>
            <select className={`${field} h-9 rounded-md border bg-background px-2`} value={f.model} onChange={set('model')}>
              {models.map((m) => <option key={m} value={m}>{MODEL_LABEL[m] || m}</option>)}
            </select>
            <span className="block text-xs text-muted-foreground">About US${per.toFixed(3)} per card — about {perMonth.toLocaleString('en-GB')} cards a month at the current budget.</span>
          </label>
          <label className="space-y-1">
            <span className="block font-medium">AI model for reading the news</span>
            <select className={`${field} h-9 rounded-md border bg-background px-2`} value={f.extract_model} onChange={set('extract_model')}>
              {models.map((m) => <option key={m} value={m}>{MODEL_LABEL[m] || m}</option>)}
            </select>
            <span className="block text-xs text-muted-foreground">Only reads headlines; no web search. Luna is enough.</span>
          </label>
          <label className="space-y-1">
            <span className="block font-medium">Thinking effort</span>
            <select className={`${field} h-9 rounded-md border bg-background px-2`} value={f.reasoning_effort} onChange={set('reasoning_effort')}>
              {['none', 'minimal', 'low', 'medium', 'high'].map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
            <span className="block text-xs text-muted-foreground">How long the model thinks before answering. Higher = slower and dearer.</span>
          </label>
          {NUM_FIELDS.map(([k, label, help, lo, hi]) => (
            <label key={k} className="space-y-1">
              <span className="block font-medium">{label}</span>
              <Input className="w-32" type="number" min={lo} max={hi} value={f[k]} onChange={set(k)} />
              {help && <span className="block text-xs text-muted-foreground">{help}</span>}
            </label>
          ))}
        </div>
        <label className="block space-y-1">
          <span className="block font-medium">Blocked websites (never used as a source)</span>
          <textarea className="w-full md:w-96 min-h-[140px] rounded-md border bg-background p-2 font-mono text-xs" value={f.blocked} onChange={set('blocked')} />
          <span className="block text-xs text-muted-foreground">One web address per line, e.g. meed.com. Paid project databases and competitors go here.</span>
        </label>
        <p className="text-xs text-muted-foreground">
          Prices used for the money counter (US$ per million words in / out): {Object.entries(s.prices || {}).map(([m, p]) => `${m} ${p[0]} / ${p[1]}`).join(' · ')} · web search US${s.search_price_per_1000} per 1,000.
        </p>
        <Button size="sm" disabled={busy} onClick={save}>Save settings</Button>
      </CardContent>
    </Card>
  );
}

// WO-64 — "Approve all": one click (and one confirmation) for everything waiting.
// Items can still be read, approved or rejected one by one below.
function ApproveAllBar({ total, call, onDone }) {
  const [kind, setKind] = useState('');
  const [asking, setAsking] = useState(false);
  const [running, setRunning] = useState(null);
  if (!total) return null;
  const label = { '': 'everything waiting', update: 'only changes to existing cards', new_project: 'only new projects (with their new companies)' };
  const go = async () => {
    setAsking(false);
    let done = 0; let failed = []; let left = null; let guard = 0;
    setRunning(0);
    try {
      while (guard < 50) {
        guard += 1;
        const r = await call('/api/research/admin/changes/approve-all', { method: 'POST', body: JSON.stringify({ kind }) });
        done += r.approved; failed = failed.concat(r.failed || []); left = r.left;
        setRunning(done);
        if (!r.left || (r.approved === 0 && !(r.failed || []).length)) break;
      }
      if (failed.length) {
        toast.error(`${done} approved. ${failed.length} could not be applied and are marked failed: ${failed.slice(0, 3).map((f) => `${f.name || ''} — ${f.reason}`).join('; ')}${failed.length > 3 ? '…' : ''}`);
      } else {
        toast.success(`${done} approved.${left ? ` ${left} still waiting (new ones arrived meanwhile).` : ''}`);
      }
    } catch (e) {
      toast.error(`${done} approved, then it stopped: ${e.message}`);
    }
    setRunning(null);
    onDone();
  };
  return (
    <div className="p-3 rounded-lg border border-primary/30 bg-primary/5 flex flex-wrap items-center gap-3" data-testid="approve-all-bar">
      {running !== null ? (
        <span className="text-sm">Approving… {running} done</span>
      ) : asking ? (
        <>
          <span className="text-sm font-medium">Approve {label[kind]}{kind ? '' : ` (${total})`}? Each change goes on its card at once (changes that cannot be applied are listed afterwards).</span>
          <Button size="sm" onClick={go} data-testid="approve-all-confirm"><Check className="w-4 h-4 mr-1" />Yes, approve</Button>
          <Button size="sm" variant="outline" onClick={() => setAsking(false)}>Cancel</Button>
        </>
      ) : (
        <>
          <Button size="sm" onClick={() => setAsking(true)} data-testid="approve-all"><Check className="w-4 h-4 mr-1" />Approve all</Button>
          <select className="h-9 rounded-md border bg-background px-2 text-sm" value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">Everything waiting ({total})</option>
            <option value="update">Only changes to existing cards</option>
            <option value="new_project">Only new projects (with their new companies)</option>
          </select>
          <span className="text-xs text-muted-foreground">Or read them below and approve or reject one by one.</span>
        </>
      )}
    </div>
  );
}

export default function AdminResearchPage() {
  const { getToken } = useAuth();
  const [ov, setOv] = useState(null);
  const [pending, setPending] = useState({ items: [], total: 0 });
  const [applied, setApplied] = useState({ items: [], total: 0 });
  const [pPage, setPPage] = useState(1);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ref, setRef] = useState('');
  const [budget, setBudget] = useState('');

  const call = useCallback(async (path, opts = {}) => {
    const res = await fetch(`${API_URL}${path}`, {
      headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' }, ...opts,
    });
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
  }, [getToken]);

  const load = useCallback(async () => {
    try {
      const [o, p, a] = await Promise.all([
        call('/api/research/admin/overview'),
        call(`/api/research/admin/changes?status=pending&page=${pPage}`),
        call('/api/research/admin/changes?status=applied&page=1'),
      ]);
      setOv(o); setPending(p); setApplied(a); setError(null);
      setBudget((b) => (b === '' ? String(o.settings.monthly_budget_usd) : b));
    } catch (e) {
      setError(e.message || 'Could not reach the server.');
    }
  }, [call, pPage]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); await load(); } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const put = (patch, ok) => act(() => call('/api/research/admin/settings', { method: 'PUT', body: JSON.stringify(patch) }), ok);
  const approve = (ch) => act(async () => {
    const r = await call(`/api/research/admin/changes/${ch._id}/approve`, { method: 'POST' });
    const failed = (r.results || []).filter((x) => x.status === 'failed');
    if (failed.length) throw new Error(`Not applied: ${failed.map((f) => f.reason).join('; ')}`);
  }, 'Approved — the card is updated.');
  const reject = (ch) => act(() => call(`/api/research/admin/changes/${ch._id}/reject`, { method: 'POST' }), 'Rejected — it will not be proposed again.');
  const undo = (ch) => act(() => call(`/api/research/admin/changes/${ch._id}/undo`, { method: 'POST' }), 'Undone — the old value is back.');
  // WO-75 — industries of the projects checked before WO-74, from the card's own text
  const reviewIndustries = () => act(async () => {
    const r = await call('/api/research/admin/industry-review', { method: 'POST', body: JSON.stringify({}) });
    toast.message(`Industry review started for ${r.due} projects — progress appears in Latest runs.`);
  });
  const checkNow = () => act(async () => {
    const r = await call('/api/research/admin/check', { method: 'POST', body: JSON.stringify({ ref_id: ref }) });
    toast.message(`Checking ${r.ref_id} ${r.name || ''} — the result appears below within about 2 minutes.`);
    setRef('');
  });

  if (error) return <div className="p-6 text-red-600" data-testid="research-error">{error} <Button size="sm" variant="outline" onClick={load}>Try again</Button></div>;
  if (!ov) return <div className="p-6 text-muted-foreground">Loading…</div>;
  const s = ov.settings, m = ov.money, n = ov.counts;

  return (
    <div className="space-y-6" data-testid="admin-research-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Sparkles className="w-6 h-6 text-primary" />Market research — ANTHONY</h1>
          <p className="text-muted-foreground">Checks project and company cards against public sources, day and night, and finds new projects in the news.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load}><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
      </div>

      <Card className="border-0 bg-card/50">
        <CardHeader>
          <CardTitle>Switch and money</CardTitle>
          <CardDescription>Stage, value, weakly sourced findings and every new record wait for you below. Small corrections with an official source (or two websites) go live by themselves and can be undone.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm w-40">Background research</span>
            {['off', 'on'].map((v) => (
              <Button key={v} size="sm" variant={s.switch === v ? 'default' : 'outline'} disabled={busy} onClick={() => put({ switch: v }, `Research switched ${v}.`)}>{v === 'on' ? 'On' : 'Off'}</Button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm w-40">Find new projects in the news</span>
            {['off', 'on'].map((v) => (
              <Button key={v} size="sm" variant={s.discovery === v ? 'default' : 'outline'} disabled={busy} onClick={() => put({ discovery: v }, `News reading switched ${v}.`)}>{v === 'on' ? 'On' : 'Off'}</Button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm w-40">Monthly budget (US$)</span>
            <Input className="w-28" type="number" min="0" value={budget} onChange={(e) => setBudget(e.target.value)} />
            <Button size="sm" variant="outline" disabled={busy} onClick={() => put({ monthly_budget_usd: Number(budget) }, 'Budget saved.')}>Save</Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div className="p-3 rounded-lg bg-muted/30"><p className="text-muted-foreground">Spent in {m.month}</p><p className="text-lg font-bold">{money(m.spent)} / {money(m.budget)}</p></div>
            <div className="p-3 rounded-lg bg-muted/30"><p className="text-muted-foreground">Today / today's share</p><p className="text-lg font-bold">{money(m.today)} / {money(m.today_allowance)}</p></div>
            <div className="p-3 rounded-lg bg-muted/30"><p className="text-muted-foreground">Projects checked</p><p className="text-lg font-bold">{n.projects_checked} / {n.projects_total}</p></div>
            <div className="p-3 rounded-lg bg-muted/30"><p className="text-muted-foreground">Companies checked</p><p className="text-lg font-bold">{n.companies_checked} / {n.companies_total}</p></div>
            <div className="p-3 rounded-lg bg-muted/30"><p className="text-muted-foreground">Checks this month</p><p className="text-lg font-bold">{n.checks_this_month}</p></div>
            <div className="p-3 rounded-lg bg-muted/30"><p className="text-muted-foreground">Changes live this month</p><p className="text-lg font-bold">{n.applied_this_month}</p></div>
            <div className="p-3 rounded-lg bg-muted/30"><p className="text-muted-foreground">New projects proposed</p><p className="text-lg font-bold">{n.new_projects_proposed_this_month}</p></div>
            <div className="p-3 rounded-lg bg-muted/30"><p className="text-muted-foreground">Waiting for you</p><p className="text-lg font-bold">{n.waiting}</p></div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm w-40">Check one card now</span>
            <Input className="w-48" placeholder="PROJ-000123 or COMP-000045" value={ref} onChange={(e) => setRef(e.target.value)} />
            <Button size="sm" disabled={busy || !ref.trim()} onClick={checkNow}>Check</Button>
          </div>
          <div className="flex flex-wrap items-center gap-3" data-testid="industry-review-row">
            <span className="text-sm w-40">Review industries</span>
            <span className="text-sm text-muted-foreground">
              {n.industries_to_review} projects checked before ANTHONY started reviewing industries still need that review (from the card's own text, no web search; unclear cards go to a normal web check).
            </span>
            <Button size="sm" variant="outline" disabled={busy || ov.industry_review_running || !n.industries_to_review} onClick={reviewIndustries}>
              {ov.industry_review_running ? 'Running…' : 'Review now'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* WO-61 — all the settings, visible */}
      <SettingsCard s={s} budget={budget} busy={busy} onSave={(patch) => put(patch, 'Settings saved.')} />

      <Card className="border-0 bg-card/50">
        <CardHeader>
          <CardTitle>Waiting for approval ({pending.total})</CardTitle>
          <CardDescription>Approve puts it on the card at once. Reject means ANTHONY will not propose the same thing again.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* WO-64 — approve everything waiting in one go */}
          <ApproveAllBar total={pending.total} call={call} onDone={() => { setPPage(1); load(); }} />
          {pending.items.length === 0 && <p className="text-sm text-muted-foreground">Nothing is waiting.</p>}
          {pending.items.map((ch) => <ChangeRow key={ch._id} ch={ch} busy={busy} onApprove={approve} onReject={reject} />)}
          {pending.total_pages > 1 && (
            <div className="flex gap-2 items-center text-sm">
              <Button size="sm" variant="outline" disabled={pPage <= 1} onClick={() => setPPage(pPage - 1)}>Previous</Button>
              <span>Page {pPage} of {pending.total_pages}</span>
              <Button size="sm" variant="outline" disabled={pPage >= pending.total_pages} onClick={() => setPPage(pPage + 1)}>Next</Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-0 bg-card/50">
        <CardHeader>
          <CardTitle>Went live by itself — latest {applied.items.length} of {applied.total}</CardTitle>
          <CardDescription>Each with its sources. Undo puts the old value back.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {applied.items.length === 0 && <p className="text-sm text-muted-foreground">Nothing yet.</p>}
          {applied.items.map((ch) => <ChangeRow key={ch._id} ch={ch} busy={busy} onUndo={undo} />)}
        </CardContent>
      </Card>

      <Card className="border-0 bg-card/50">
        <CardHeader><CardTitle>Weekly market note</CardTitle></CardHeader>
        <CardContent>
          {ov.digest ? <p className="text-sm whitespace-pre-wrap">{ov.digest.text}</p> : <p className="text-sm text-muted-foreground">The first note is written after the first week of research.</p>}
        </CardContent>
      </Card>

      <Card className="border-0 bg-card/50">
        <CardHeader><CardTitle>Latest runs</CardTitle></CardHeader>
        <CardContent className="space-y-1 text-sm">
          {ov.runs.length === 0 && <p className="text-muted-foreground">No run yet.</p>}
          {ov.runs.map((r) => (
            <div key={r._id} className="flex flex-wrap gap-3 border-b border-muted py-1">
              <span className="w-32 text-muted-foreground">{when(r.started_at)}</span>
              <span className="w-28">{r.kind}</span>
              <span className="flex-1">{r.ref_id ? `${r.ref_id} · ` : ''}{r.name}</span>
              <span className="w-28">{r.outcome}{r.error ? ` — ${r.error}` : ''}</span>
              <span className="w-24 text-right">{r.applied ? `${r.applied} live` : ''}{r.pending ? ` ${r.pending} waiting` : ''}</span>
              <span className="w-20 text-right text-muted-foreground">{money(r.usd)}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
